import { FC, Fragment, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	MenuItem,
	OutlinedInput,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TableRow,
	TableSortLabel,
	TextField,
	Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import IconButton from "@mui/material/IconButton";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useQuery } from "@tanstack/react-query";
import { useSettings } from "../../../../../hooks/useSetting";
import { useDateFormat } from "../../../../../hooks/useDateFormat";
import { useGroupLabels } from "../../../../../hooks/groupLabels";
import { useGetProviderSitesByOUId } from "../../../../../hooks/useBeneficiaries";
import { useUserStore } from "../../../../../stores/user.store";
import {
	DisbursedIncentiveRow,
	DisbursedIncentivesResponse,
	DisbursementVisitsResponse,
	reportsService,
} from "../../../../../services/reports.service";
import DisbursementVisitsPanel from "./DisbursementVisitsPanel";
import { triggerDownload } from "../../../../../utils/xlsx-export/build-workbook";
import {
	buildDisbursedFilename,
	buildDisbursedIncentivesWorkbook,
} from "../../../../../utils/xlsx-export/disbursed-incentives-workbook";
import { useAppTheme } from "../../../../../utils/theme";
import BasicDatepicker from "../../../../../components/BasicDatepicker/BasicDatepicker";
import {
	FilterField,
	NoProgramCard,
	OrgUnitFilterField,
	ProgramFilterField,
	ReportActions,
	ReportTabProps,
	groupLabelsForSites,
} from "../common";
import { combinedOrgUnitIds } from "../../../../../utils/programSelection";

const PAGE_SIZE = 25;

interface Option {
	id: string;
	name: string | null;
}

const toOptions = (
	list: ReadonlyArray<{ _id?: string; id?: string; name?: string | null }>,
): Option[] =>
	(list ?? []).map((o) => ({ id: o._id ?? o.id ?? "", name: o.name ?? null }));

/**
 * Disbursed Incentives report — the Disbursements (header) screen.
 *
 * Epic SIMS-8119. SIMS-8161 gated the tab, SIMS-8120 supplies the table/total/
 * sort, SIMS-8123 the filter semantics, SIMS-8151 the CIN search and SIMS-8155
 * the exports. The per-disbursement visits drill-down is a separate Epic
 * (SIMS-8177), which is why rows here are inert and there is no expand column.
 *
 * Every colour comes from `useAppTheme()`, which is driven by the account's
 * themeColors setting — the Hi-Fi's literal hexes are one account's rendering of
 * those tokens, not values to hardcode. The beneficiary term and currency symbol
 * likewise come from the account's settings, resolved server-side so the screen
 * and the SIMS-8155 export cannot render different ones.
 */
const DisbursedIncentivesTab: FC<ReportTabProps> = ({
	programId,
	programIds,
	programs,
	onProgramChange,
	onProgramsChange,
}) => {
	const theme = useAppTheme();
	const { formatDate, dateFormat } = useDateFormat();
	const { data: settings } = useSettings();
	const { userInfo } = useUserStore();

	// Design tokens → theme palette (driven by the account's themeColors).
	const c = {
		border: theme.palette.outline.light,
		divider: theme.palette.outline.light,
		band: theme.palette.surface.main,
		label: theme.palette.textSecondary.main,
		muted: theme.palette.textSecondary.main,
		ink: theme.palette.textPrimary.main,
		accent: theme.palette.primary.main,
		error: theme.palette.error.main,
	};

	const selected = programIds ?? [];

	// --- filter state (applied only on Run — SIMS-8023's model) ---------------
	const [orgUnitIds, setOrgUnitIds] = useState<string[]>([]);
	const [groupLabelIds, setGroupLabelIds] = useState<string[]>([]);
	const [providerId, setProviderId] = useState("");
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [cinInput, setCinInput] = useState("");

	const [applied, setApplied] = useState<null | {
		programId: string;
		orgUnitIds?: string;
		groupLabelIds?: string;
		providerId?: string;
		startDate?: string;
		endDate?: string;
		cin?: string;
	}>(null);
	const [pageNumber, setPageNumber] = useState(0);
	const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

	const assignedOuIds = combinedOrgUnitIds(userInfo?.programs, selected);
	const { data: groupLabels } = useGroupLabels();
	const { data: providerSites } = useGetProviderSitesByOUId({
		ouIds: assignedOuIds,
		queryKey: "sims-disbursed-provider-sites",
	});

	const { data, isFetching } = useQuery<DisbursedIncentivesResponse>({
		queryKey: ["reports-disbursed-incentives", applied, pageNumber, sortDir],
		// SIMS-8023 AC1: nothing runs until Run report is pressed.
		enabled: applied !== null,
		queryFn: () =>
			reportsService.getDisbursedIncentives({
				...(applied as { programId: string }),
				pageNumber,
				pageSize: PAGE_SIZE,
				sortDir,
			}),
		// AC5: the previous page stays on screen while the next one loads, so a
		// sort or a page change never blanks the table.
		placeholderData: (prev) => prev,
	});

	const siteOptions = toOptions(providerSites ?? []);
	const groupLabelOptions = toOptions(
		groupLabelsForSites(groupLabels, providerSites),
	);
	const providerOptions = toOptions(data?.availableProviders ?? []);

	// AC6: both come from the account's settings, resolved server-side.
	const symbol = data?.currencySymbol ?? "";
	const heading = data?.labels.beneficiaryHeading ?? "";
	const money = (n: number): string => `${symbol}${(n ?? 0).toFixed(2)}`;

	const datesInvalid =
		!!startDate && !!endDate && dayjs(endDate).isBefore(dayjs(startDate));
	const canRun = selected.length > 0 && !datesInvalid;

	/** SIMS-8023 AC3: changing a filter clears the results until Run is pressed. */
	const onFilterChange = (fn: () => void): void => {
		fn();
		setApplied(null);
		setPageNumber(0);
	};

	const runReport = (): void => {
		setPageNumber(0);
		setApplied({
			programId: selected.join(","),
			orgUnitIds: orgUnitIds.length ? orgUnitIds.join(",") : undefined,
			groupLabelIds: groupLabelIds.length ? groupLabelIds.join(",") : undefined,
			// "All providers" is the UNSET state, not a value — send nothing.
			providerId: providerId || undefined,
			startDate: startDate || undefined,
			endDate: endDate || undefined,
			cin: cinInput.trim() || undefined,
		});
	};

	const clearAll = (): void => {
		setOrgUnitIds([]);
		setGroupLabelIds([]);
		setProviderId("");
		setStartDate("");
		setEndDate("");
		setCinInput("");
		setApplied(null);
		setPageNumber(0);
	};

	// Which export is running, not merely whether one is — so the button being
	// used shows the progress, matching Visit Detail.
	const [exporting, setExporting] = useState<"" | "excel" | "pdf">("");
	// Surfaced next to the results rather than thrown away — a download that
	// silently does nothing is indistinguishable from a slow one.
	const [error, setError] = useState<string | null>(null);

	const selectedProgramNames = programs
		.filter((p) => selected.includes(p._id))
		.map((p) => p.name ?? "")
		.filter(Boolean)
		.sort((a, b) => a.localeCompare(b));

	/**
	 * SIMS-8155 — the rows for a file.
	 *
	 * Re-fetches the WHOLE filtered set: AC1's "the on-screen rows" means the
	 * report, not the page being looked at, the same way AC2's total is set-wide.
	 * They come back already masked, so a withheld name is an em dash before this
	 * code ever sees it (AC3).
	 */
	const exportRows = async (): Promise<DisbursedIncentiveRow[]> =>
		(
			await reportsService.fetchAllDisbursedIncentives({
				...(applied as { programId: string }),
				sortDir,
			})
		).rows;

	const handleExcel = async (): Promise<void> => {
		if (!data) return;
		setExporting("excel");
		try {
			const built = await buildDisbursedIncentivesWorkbook({
				...(applied as { programId: string }),
				rows: await exportRows(),
				// From the report, never re-summed from the exported rows — those are
				// capped, and a file whose total disagreed with the screen is exactly
				// the discrepancy this report exists to prevent.
				totalDisbursed: data.totalDisbursed,
				// AC4: the values the SCREEN rendered, passed through.
				beneficiaryHeading: data.labels.beneficiaryHeading,
				currencySymbol: data.currencySymbol,
				programNames: selectedProgramNames,
			});
			triggerDownload(built);
		} catch {
			setError("The Excel export could not be generated.");
		} finally {
			setExporting("");
		}
	};

	const handlePdf = async (): Promise<void> => {
		if (!data) return;
		setExporting("pdf");
		try {
			const all = await exportRows();
			const doc = new jsPDF({ orientation: "landscape" });

			doc.setFontSize(14);
			doc.text("Disbursed Incentives Report", 14, 16);
			doc.setFontSize(9);
			// AC2: the PDF lists the selected programs.
			doc.text(`Programs: ${selectedProgramNames.join(", ") || "-"}`, 14, 22);
			doc.text(`Generated ${dayjs().format(dateFormat)}`, 14, 27);

			autoTable(doc, {
				startY: 32,
				head: [
					[
						"Program",
						"Transaction Date",
						"CIN",
						// AC4: the same heading the screen showed.
						data.labels.beneficiaryHeading,
						// AC2: vendor name + delivery.
						"Vendor Card",
						"Delivery",
						"Amount",
					],
				],
				body: all.map((r) => [
					r.programName,
					formatDate(r.transactionDate),
					r.cin,
					// AC3: already an em dash when the account withholds names.
					r.beneficiaryName,
					r.vendorCard,
					r.delivery,
					money(r.amount),
				]),
				foot: [
					["Total disbursed", "", "", "", "", "", money(data.totalDisbursed)],
				],
				styles: { fontSize: 8 },
			});

			doc.save(buildDisbursedFilename(selectedProgramNames, "pdf"));
		} catch {
			setError("The PDF export could not be generated.");
		} finally {
			setExporting("");
		}
	};

	/**
	 * AC5: reorders the whole filtered set server-side and returns to page 1.
	 * Deliberately not the shared client-side table sort, which compares the
	 * RENDERED date as a string — that puts 03/14/2026 before 12/01/2025.
	 */
	/**
	 * SIMS-8131 — which disbursement is expanded, and what came back for it.
	 *
	 * Keyed by transactionId rather than row index: the index shifts under a sort
	 * or a page change, which would leave the wrong row open showing another
	 * payout's visits.
	 */
	const [expandedId, setExpandedId] = useState<string | null>(null);
	const [visitsByTxn, setVisitsByTxn] = useState<
		Record<string, DisbursementVisitsResponse>
	>({});
	const [visitsLoading, setVisitsLoading] = useState<string | null>(null);
	const [visitsError, setVisitsError] = useState<Record<string, string>>({});

	const toggleExpand = (transactionId: string): void => {
		if (expandedId === transactionId) {
			setExpandedId(null);
			return;
		}
		setExpandedId(transactionId);

		// Fetched once per disbursement and kept — re-collapsing and re-expanding
		// the same row should not re-hit the API, and the visits behind a past
		// payout do not change while the report is open.
		if (visitsByTxn[transactionId]) return;

		setVisitsLoading(transactionId);
		setVisitsError((prev) => {
			const next = { ...prev };
			delete next[transactionId];
			return next;
		});
		void reportsService
			.getDisbursementVisits({
				programId: selected.join(","),
				transactionId,
				orgUnitIds: orgUnitIds.length ? orgUnitIds.join(",") : undefined,
			})
			.then((res) => {
				setVisitsByTxn((prev) => ({ ...prev, [transactionId]: res }));
			})
			.catch(() => {
				setVisitsError((prev) => ({
					...prev,
					[transactionId]:
						"The visits for this disbursement could not be loaded.",
				}));
			})
			.finally(() => setVisitsLoading(null));
	};

	const toggleSort = (): void => {
		setSortDir((prev) => (prev === "desc" ? "asc" : "desc"));
		setPageNumber(0);
	};

	if (selected.length === 0) {
		return (
			<Stack spacing={3}>
				<NoProgramCard borderColor={c.border} mutedColor={c.muted} />
			</Stack>
		);
	}

	const rows = data?.rows ?? [];
	const total = data?.pagination.total ?? 0;
	const appliedDir = data?.sortDir ?? sortDir;
	const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
	const from = total === 0 ? 0 : pageNumber * PAGE_SIZE + 1;
	const to = Math.min(total, (pageNumber + 1) * PAGE_SIZE);

	return (
		<Stack spacing={3}>
			{/* Report header — title + exports (SIMS-8155 wires the handlers). */}
			<Paper
				variant="outlined"
				sx={{ borderColor: c.border, borderRadius: 3, p: 3 }}
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-start",
						gap: 4,
					}}
				>
					<Box>
						<Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
							Disbursed Incentives Report
						</Typography>
						<Typography variant="body2" sx={{ color: c.muted }}>
							{settings?.brandInformation?.title ?? "Reports"} / Disbursed
							incentives
						</Typography>
						<Typography variant="body2" sx={{ color: c.muted }}>
							Generated {dayjs().format(dateFormat)}
						</Typography>
					</Box>
					{/*
					 * SIMS-8155. Same shape as Visit Detail's: a spinner replaces the
					 * download icon on the button being used and the label reads
					 * "Exporting…", so the user can tell WHICH export is running —
					 * with one shared flag both buttons just grey out together and a
					 * slow export is indistinguishable from a dead one.
					 *
					 * Disabled before a run: the Hi-Fi draws them always enabled, but
					 * exporting a report the user has not run yields an empty file.
					 */}
					<Stack direction="row" spacing={2} sx={{ flex: "none" }}>
						<Button
							variant="outlined"
							startIcon={
								exporting === "excel" ? (
									<CircularProgress size={16} color="inherit" />
								) : (
									<FileDownloadOutlinedIcon />
								)
							}
							disabled={!data || exporting !== ""}
							onClick={() => void handleExcel()}
						>
							{exporting === "excel" ? "Exporting…" : "Excel"}
						</Button>
						<Button
							variant="outlined"
							startIcon={
								exporting === "pdf" ? (
									<CircularProgress size={16} color="inherit" />
								) : (
									<FileDownloadOutlinedIcon />
								)
							}
							disabled={!data || exporting !== ""}
							onClick={() => void handlePdf()}
						>
							{exporting === "pdf" ? "Exporting…" : "PDF"}
						</Button>
					</Stack>
				</Box>
			</Paper>

			{/* Filters — SIMS-8123. */}
			<Paper
				variant="outlined"
				sx={{ borderColor: c.border, borderRadius: 3, p: 3 }}
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						gap: 3,
						mb: 2,
					}}
				>
					<Typography sx={{ fontWeight: 600 }}>Filters</Typography>
					{error && (
						<Typography role="alert" variant="caption" sx={{ color: c.error }}>
							{error}
						</Typography>
					)}
					{/* AC1 stated on screen, next to the controls it governs. */}
					<Typography variant="caption" sx={{ color: c.muted }}>
						Date range filters the disbursement date, not the dates of the
						visits it covered.
					</Typography>
				</Box>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: {
							xs: "1fr",
							sm: "repeat(2,1fr)",
							md: "repeat(4,1fr)",
						},
						gap: 3,
						alignItems: "end",
					}}
				>
					<ProgramFilterField
						programId={programId}
						programIds={selected}
						programs={programs}
						onProgramChange={(id) => onFilterChange(() => onProgramChange(id))}
						onProgramsChange={(ids) =>
							onFilterChange(() => {
								onProgramsChange?.(ids);
								// Every other filter is program-scoped, so a stale
								// selection would silently narrow the new programs.
								setOrgUnitIds([]);
								setGroupLabelIds([]);
								setProviderId("");
							})
						}
						labelColor={c.label}
						mutedColor={c.muted}
						errorColor={c.error}
					/>

					<OrgUnitFilterField
						userPrograms={userInfo?.programs}
						selectedProgramIds={selected}
						programs={programs}
						options={siteOptions}
						value={orgUnitIds}
						onChange={(ids) => onFilterChange(() => setOrgUnitIds(ids))}
						labelColor={c.label}
						mutedColor={c.muted}
					/>

					<FilterField label="Group labels" labelColor={c.label}>
						<Select
							multiple
							size="small"
							displayEmpty
							value={groupLabelIds}
							input={<OutlinedInput />}
							onChange={(e) =>
								onFilterChange(() =>
									setGroupLabelIds(e.target.value as string[]),
								)
							}
							renderValue={(v) =>
								(v as string[]).length === 0 ? (
									<Typography sx={{ color: c.muted }}>
										All group labels
									</Typography>
								) : (
									(v as string[])
										.map(
											(id) =>
												groupLabelOptions.find((o) => o.id === id)?.name ?? id,
										)
										.join(", ")
								)
							}
						>
							{groupLabelOptions.map((o) => (
								<MenuItem key={o.id} value={o.id}>
									{o.name ?? o.id}
								</MenuItem>
							))}
						</Select>
					</FilterField>

					{/* AC2 — single-select, and the caption says what it means. */}
					<FilterField
						label="Provider"
						helperText="Disbursements processed by this provider"
						labelColor={c.label}
					>
						<Select
							size="small"
							displayEmpty
							value={providerId}
							input={<OutlinedInput />}
							onChange={(e) =>
								onFilterChange(() => setProviderId(e.target.value))
							}
							renderValue={(v) =>
								v ? (
									(providerOptions.find((o) => o.id === v)?.name ?? v)
								) : (
									<Typography sx={{ color: c.muted }}>All providers</Typography>
								)
							}
						>
							<MenuItem value="">All providers</MenuItem>
							{providerOptions.map((o) => (
								<MenuItem key={o.id} value={o.id}>
									{o.name ?? o.id}
								</MenuItem>
							))}
						</Select>
					</FilterField>

					<FilterField label="Start date" newRow labelColor={c.label}>
						<BasicDatepicker
							label="Start date"
							format={dateFormat}
							disableFuture={false}
							value={startDate ? dayjs(startDate) : null}
							onChange={(d) =>
								onFilterChange(() =>
									setStartDate(d && d.isValid() ? d.format("YYYY-MM-DD") : ""),
								)
							}
						/>
					</FilterField>

					<FilterField
						label="End date"
						labelColor={c.label}
						errorColor={c.error}
						// SIMS-8895 item 2 records that this report family accepts an
						// end before a start with no warning. Not repeated here.
						helperText={
							datesInvalid ? "End date must not precede the start date." : ""
						}
					>
						<BasicDatepicker
							label="End date"
							format={dateFormat}
							disableFuture={false}
							value={endDate ? dayjs(endDate) : null}
							onChange={(d) =>
								onFilterChange(() =>
									setEndDate(d && d.isValid() ? d.format("YYYY-MM-DD") : ""),
								)
							}
						/>
					</FilterField>

					{/* SIMS-8151 — exact-match CIN, applied on Run. */}
					<FilterField label="CIN" labelColor={c.label}>
						<TextField
							size="small"
							placeholder="Search by CIN"
							value={cinInput}
							inputProps={{ "aria-label": "Search by CIN" }}
							onChange={(e) =>
								onFilterChange(() => setCinInput(e.target.value))
							}
						/>
					</FilterField>

					{/*
					 * Clear all + Run report, PAIRED on their own full-width row.
					 *
					 * ReportActions renders TWO grid cells and relies on exactly two
					 * free cells at the end of a row. This report's grid leaves only
					 * one — row 2 is Start date / End date / CIN — so Clear all took
					 * the last cell of row 2 and Run report wrapped to row 3, which is
					 * what made the pair look broken.
					 *
					 * Spanning the full row keeps them together and right-aligned, as
					 * Program Activity Summary pairs them. The usual warning against
					 * wrapping ReportActions is about collapsing into a 112px trailing
					 * column; these columns are equal 1fr, so there is nothing to
					 * collapse into.
					 */}
					<Box
						sx={{
							gridColumn: "1 / -1",
							display: "flex",
							alignItems: "flex-end",
							justifyContent: "flex-end",
							gap: 2,
						}}
					>
						<ReportActions
							onClearAll={clearAll}
							onRun={runReport}
							canRun={canRun}
						/>
					</Box>
				</Box>
			</Paper>

			{/* Results — SIMS-8120. */}
			{applied === null ? null : (
				<Paper
					variant="outlined"
					sx={{ borderColor: c.border, borderRadius: 3, overflow: "hidden" }}
				>
					<Box
						sx={{
							p: 3,
							borderBottom: `1px solid ${c.divider}`,
							display: "flex",
							justifyContent: "space-between",
							alignItems: "baseline",
							gap: 3,
						}}
					>
						<Typography sx={{ fontWeight: 600 }}>Disbursements</Typography>
						<Typography
							role="status"
							aria-live="polite"
							variant="caption"
							sx={{ color: c.muted, textAlign: "right" }}
						>
							{total === 0
								? "No disbursements match these filters."
								: `${selected.length} program(s) · ${from}–${to} of ${total} disbursements · sorted by transaction date, ${
										appliedDir === "desc" ? "newest first" : "oldest first"
									}`}
						</Typography>
					</Box>

					{isFetching && !data ? (
						<Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
							<CircularProgress aria-label="Loading disbursements" />
						</Box>
					) : (
						<>
							{/* overflow-x on the SCROLL container: the Hi-Fi's
							    overflow:hidden clips the last columns with no way to
							    reach them on a narrower viewport (WCAG 1.4.10). */}
							<TableContainer
								sx={{ overflowX: "auto", opacity: isFetching ? 0.5 : 1 }}
							>
								<Table size="small" aria-label="Disbursements">
									<TableHead>
										<TableRow sx={{ bgcolor: c.band }}>
											{/* SIMS-8131: the 8th column SIMS-8120 deliberately did
											    not ship, rather than draw an affordance that
											    expanded nothing. Header cell is empty by design —
											    the control in each row is self-labelling. */}
											<TableCell sx={{ width: 48 }} />
											<TableCell>Program</TableCell>
											<TableCell
												sortDirection={appliedDir}
												aria-sort={
													appliedDir === "desc" ? "descending" : "ascending"
												}
											>
												{/* A real control — the Hi-Fi draws inert text. */}
												<TableSortLabel
													active
													direction={appliedDir}
													onClick={toggleSort}
													sx={{ color: c.accent }}
												>
													Transaction Date
												</TableSortLabel>
											</TableCell>
											<TableCell>CIN</TableCell>
											<TableCell>{heading}</TableCell>
											<TableCell>Vendor Card</TableCell>
											<TableCell>Delivery</TableCell>
											<TableCell align="right">Amount</TableCell>
										</TableRow>
									</TableHead>

									<TableBody>
										{rows.map((row) => {
											const isOpen = expandedId === row.transactionId;
											const panelId = `disbursement-visits-${row.transactionId}`;
											return (
												<Fragment key={row.transactionId}>
													<TableRow hover>
														<TableCell sx={{ width: 48, pr: 0 }}>
															{/*
															 * A REAL button, not the drawing's row-level
															 * onClick on an inert <span>. Wiring the click to
															 * the whole row makes one tab stop swallow seven
															 * data cells, is not keyboard-operable (WCAG
															 * 2.1.1), and stops a reader selecting a CIN with
															 * the mouse. Same call SIMS-8120 made for the
															 * sort header.
															 */}
															<IconButton
																size="small"
																aria-expanded={isOpen}
																aria-controls={isOpen ? panelId : undefined}
																aria-label={`${isOpen ? "Hide" : "Show"} visits for the ${formatDate(row.transactionDate)} disbursement`}
																onClick={() => toggleExpand(row.transactionId)}
																sx={{ color: c.accent }}
															>
																{isOpen ? (
																	<ExpandMoreIcon fontSize="small" />
																) : (
																	<ChevronRightIcon fontSize="small" />
																)}
															</IconButton>
														</TableCell>
														<TableCell>{row.programName}</TableCell>
														<TableCell
															sx={{ fontVariantNumeric: "tabular-nums" }}
														>
															{formatDate(row.transactionDate)}
														</TableCell>
														<TableCell
															sx={{ fontVariantNumeric: "tabular-nums" }}
														>
															{row.cin}
														</TableCell>
														<TableCell>{row.beneficiaryName}</TableCell>
														<TableCell>{row.vendorCard}</TableCell>
														<TableCell sx={{ color: c.muted }}>
															{row.delivery}
														</TableCell>
														<TableCell
															align="right"
															sx={{
																fontWeight: 500,
																fontVariantNumeric: "tabular-nums",
															}}
														>
															{money(row.amount)}
														</TableCell>
													</TableRow>
													{isOpen && (
														<TableRow>
															{/* One full-width cell: the panel is a detail OF
													    this row, so it must not introduce a second
													    column rhythm the header does not describe. */}
															<TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
																<DisbursementVisitsPanel
																	id={panelId}
																	visits={
																		visitsByTxn[row.transactionId]?.visits ?? []
																	}
																	earnedTotal={
																		visitsByTxn[row.transactionId]
																			?.earnedTotal ?? 0
																	}
																	disbursedAmount={
																		visitsByTxn[row.transactionId]
																			?.disbursedAmount ?? row.amount
																	}
																	currencySymbol={
																		visitsByTxn[row.transactionId]
																			?.currencySymbol ?? symbol
																	}
																	isLoading={
																		visitsLoading === row.transactionId
																	}
																	error={visitsError[row.transactionId] ?? null}
																	colors={c}
																	formatDate={formatDate}
																/>
															</TableCell>
														</TableRow>
													)}
												</Fragment>
											);
										})}
									</TableBody>

									{/* AC2/AC5: a real tfoot, outside the sortable rows. */}
									<TableFooter>
										<TableRow
											sx={{
												bgcolor: c.band,
												borderTop: `2px solid ${c.accent}`,
											}}
										>
											{/* SIMS-8131: the chevron column re-spans this row.
											    It was 6+1 for seven columns; it is 7+1 now. A
											    stale span silently misaligns the grand total
											    under the wrong column. */}
											<TableCell colSpan={7} sx={{ color: c.ink }}>
												<strong>
													All selected programs — Total disbursed · {total}{" "}
													disbursements
												</strong>
											</TableCell>
											<TableCell
												align="right"
												sx={{
													color: c.accent,
													fontWeight: 700,
													fontSize: 18,
													fontVariantNumeric: "tabular-nums",
												}}
											>
												{money(data?.totalDisbursed ?? 0)}
											</TableCell>
										</TableRow>
									</TableFooter>
								</Table>
							</TableContainer>

							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									p: 2,
									borderTop: `1px solid ${c.divider}`,
								}}
							>
								<Typography variant="caption" sx={{ color: c.muted }}>
									{from}–{to} of {total} disbursements
								</Typography>
								<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
									<Button
										size="small"
										disabled={pageNumber === 0}
										onClick={() => setPageNumber((p) => Math.max(0, p - 1))}
									>
										Prev
									</Button>
									<Typography variant="caption" sx={{ color: c.muted }}>
										Page {pageNumber + 1} of {pageCount}
									</Typography>
									<Button
										size="small"
										disabled={pageNumber + 1 >= pageCount}
										onClick={() => setPageNumber((p) => p + 1)}
									>
										Next
									</Button>
								</Box>
							</Box>
						</>
					)}

					<Typography
						variant="caption"
						sx={{ color: c.muted, display: "block", p: 2, pt: 0 }}
					>
						Total disbursed reconciles with the Program Activity Summary&apos;s
						Total Disbursed under the same filters.
					</Typography>
				</Paper>
			)}
		</Stack>
	);
};

export default DisbursedIncentivesTab;
